import React, { Suspense, useContext, useEffect, useState } from 'react';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import { BrowserRouter, Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';

import { Bundles, CatalogEventsPage, ItemInformation, ItemInformationUpload, Taxonomy } from 'components/catalog';
import { Catalog } from 'components/catalog/catalog';
import { MassCredit } from 'components/compensations';
import {
    CarrierLookup,
    CatalogSummary,
    CustManage,
    CustomerManagerBulkTools,
    PrivacyRequests,
    ServiceDelayBanners,
} from 'components/custmanage';
import {
    BAPerfectOrderReportPage,
    FBMApiDocs,
    FBMAsn,
    FBMCatalog,
    FBMIngestionResults,
    FBMInventory,
    FBMManageAccounts,
    FBMPackagingAsn,
    FBMSubstitutionRules,
    FBMUserAccounts,
    FBMViewOrders,
    FBMViewReports,
    FBMViewShipments,
    LittleSpoonInventoryPlanningPage,
    LittleSpoonSubList,
    ViewFBMShipmentHospital,
    ViewPartnerPurchaseOrdersPage,
} from 'components/fbm';
import { DriverTipsReports, FinanceUploads, Payments } from 'components/finance';
import {
    GoPuffMicroFC,
    GoPuffOrderViewer,
    GoPuffTMSUpload,
    IssueGoPuffCredits,
    ViewGoPuffBatches,
    ViewGoPuffInvoices,
    ViewGoPuffPackout,
    ViewGoPuffPricing,
} from 'components/gopuff';
import { EmailCampaigns, NPSResults } from 'components/growth';
import {
    EmployeeBox,
    FoodAsMedicineAccounts,
    ITSKnowledgeBase,
    JalapenoAccounts,
    NetSuiteAI,
    ViewJalapenoProfiles,
    ViewOnlyJalapenoProfiles,
} from 'components/infoTech';
import { InventorySeparation } from 'components/inventory/InventorySeparation';
import {
    BulkReassignLineHaul,
    CreateCrossDockPurchaseOrder,
    CrossDockPurchaseOrders,
    DBICrossDocks,
    DbiManagement,
    FBMZipMapUpload,
    InboundCrossDocks,
    InboundTrucks,
    LineHaulManagement,
    ManifestTrucks,
    OutboundTrucks,
    ReassignPurchasedShipments,
    ShippingRateUpload,
    ViewCrossDockPurchaseOrderEntity,
    ViewHolidayOffsets,
    ZipMapResults,
    ZipMapUpload,
} from 'components/logistics';
import { FulfillmentLifecycleDashboard } from 'components/logistics/fulfillmentLifecycle';
import {
    AcquisitionPDPs,
    Discounts,
    ProduceList,
    ReferralProgram,
    SearchBoostComponent,
    SpecialOrdersMarketing,
    SystemGiftCodes,
    VendorPromotions,
    VipCustomers,
} from 'components/marketing';
import { NavDrawer } from 'components/nav-bar/nav-drawer';
import { BamsShorts, InventoryHistoryV2, NegativeVariance, ShipmentStatus, ShrinkLog } from 'components/opsVisibility';
import {
    AssignPackaging,
    AutoAssignPackaging,
    Packaging,
    PackagingPurchaseOrder,
    WeatherReport,
} from 'components/packaging';
import { BulkCreatePackagingPOV2 } from 'components/packaging/components/packagingPOV2/BulkCreatePackagingPOV2';
import { CreatePackagingPOV2 } from 'components/packaging/components/packagingPOV2/CreatePackagingPOV2';
import { ViewPackagingPOEntity } from 'components/packaging/components/packagingPOV2/entityView/ViewPackagingPOEntity';
import { ViewPackagingPOV2 } from 'components/packaging/components/packagingPOV2/ViewPackagingPOV2';
import { Drinks, FAMPartners, ViewFAMOrders } from 'components/partnerships';
import InventoryPrepTabs from 'components/prep';
import { CreditsTracking, Shippers } from 'components/procurement';
import { BulkCreatePurchaseOrder } from 'components/procurement/components/purchaseOrdersV2/BulkCreatePurchaseOrder';
import { CreatePurchaseOrder } from 'components/procurement/components/purchaseOrdersV2/CreatePurchaseOrder';
import { ViewPurchaseOrderEntity } from 'components/procurement/components/purchaseOrdersV2/entityView/ViewPurchaseOrderEntity';
import { ViewPurchaseOrdersV2 } from 'components/procurement/components/purchaseOrdersV2/ViewPurchaseOrdersV2';
import {
    CartExperience,
    Cohort,
    DriverTipsSettings,
    FeatureFlag,
    OutOfBoxAvailability,
    PlusMembershipOffers,
} from 'components/product';
import {
    BannerCreation,
    Carousels,
    CarouselUpload,
    PackGroups,
    PricingZones,
    SearchConfig,
    SellableItemInventory,
    SellableItemMisfitsPlusDiscounts,
    SellableItemPrices,
    SellableItems,
    ViewPricing,
} from 'components/production';
import AisleContent from 'components/production/components/AisleContent';
import { QualityControlReceiving, ViewCCOF, ViewQCAuditTasks } from 'components/quality';
import { InboundReceiving, PurchaseOrderDocuments, PurchaseOrderDocumentsV2, Receiving } from 'components/receiving';
import {
    Batchelor,
    BatchelorReprint,
    DBIDeepDive,
    FailedToPurchase,
    PickupBoxes,
    PrintLabels,
    PurchaseCarrierShipLabels,
    PurchaseLabels,
    StuckShipments,
    ViewBatches,
} from 'components/shipping';
import StorefrontConfigEditor from 'components/storefront/StorefrontConfigEditor';
import StorefrontConfigList from 'components/storefront/StorefrontConfigList';
import {
    CCPADashboard,
    LiveInventory,
    MarketplaceDrinksInventory,
    MarketplaceInventory,
    OrderDeepDive,
} from 'components/system';
import { Training } from 'components/training';
import { HomeScreen } from 'components/user';
import { Vendor } from 'components/vendor';
import {
    CreateFCTransfer,
    CubeLevels,
    CycleCount,
    FeatureFlagManagement,
    InternalTransfers,
    LocationInventory,
    LocationsV2,
    MispickHandling,
    Netsuite,
    OosLogPage,
    PackLines,
    PackLineZones,
    RackLabels,
    Slotting,
    ViewAcceptTransfer,
    ViewAllFCTransfers,
    ViewFCTransferEntity,
    ViewFCTransfers,
} from 'components/warehouse';
import {
    ReviewWholesaleCredits,
    WholesaleAdjustmentUpload,
    WholesaleOrderUpload,
    WholesalePricingUpload,
} from 'components/wholesale/shared';
import { AuthProvider, useAuth } from 'contexts/AuthContext';
import { DataTableProvider } from 'contexts/TableConfiguration';
import { UserContext, UserProvider } from 'contexts/UserContext';
import { FamApp } from 'foodAsMedicineComponents';
import { SpecialOrders } from 'shared';

import './app.css';

const colors = {
    mustard: '#F1C34A',
    orange: '#DEA455',
    pepper: '#DE8255',
    tomato: '#DC6B31',
    ivy: '#4C884B',
    aloe: '#B0C472',
    bone: '#F2EBD1',
    white: '#FFFFFF',
    lightGray: '#F6F6F6',
    medGray: '#C3C3C3',
    darkGray: '#969696',
    black: '#2d2d2d',
    texas: '#94122B',
    chiyellow: '#D7AC2B',
    pink: '#B32274',
    purple: '#89099B',
    teal: '#63ACDE',
    fern: '#2A7E3E',
};

const theme = createMuiTheme({
    palette: {
        secondary: {
            main: colors.tomato,
            dark: colors.pepper,
        },
        background: {
            default: colors.lightGray,
        },
        medGray: {
            main: colors.medGray,
        },
        darkGray: {
            main: colors.darkGray,
        },
        mustard: {
            main: colors.mustard,
        },
        bone: {
            main: colors.bone,
        },
        orange: {
            main: colors.orange,
        },
        pepper: {
            main: colors.pepper,
        },
        tomato: {
            main: colors.tomato,
        },
        aloe: {
            main: colors.aloe,
        },
        ivy: {
            main: colors.ivy,
        },
        texas: {
            main: colors.texas,
        },
        black: {
            main: colors.black,
        },
        chiyellow: {
            main: colors.chiyellow,
        },
        pink: {
            main: colors.pink,
        },
        purple: {
            main: colors.purple,
        },
        teal: {
            main: colors.teal,
        },
        fern: {
            main: colors.fern,
        },
    },
});

const AuthPageFallback = <div className="flex h-64 w-full items-center justify-center">Loading...</div>;

const Login = React.lazy(() =>
    import('components/login/login').then((module) => ({
        default: module.Login,
    })),
);

const ForgotPassword = React.lazy(() =>
    import('components/login/ForgotPassword').then((module) => ({
        default: module.ForgotPassword,
    })),
);

const ChangePassword = React.lazy(() =>
    import('components/login/ChangePassword').then((module) => ({
        default: module.ChangePassword,
    })),
);

function App() {
    return (
        <ThemeProvider theme={theme}>
            <AuthProvider>
                <UserProvider>
                    <DataTableProvider>
                        <BrowserRouter basename="/lettuce">
                            <AppSelector />
                            <Toaster />
                        </BrowserRouter>
                    </DataTableProvider>
                </UserProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

function MMApp() {
    const { authStatus, challengeUser } = useAuth();
    const location = useLocation();
    const history = useHistory(); // Add useHistory

    // Check if user is authenticated (has an id from localStorage/context)
    // const isAuthenticated = !!user?.id;
    const isLoginPage = location.pathname === '/login';
    const isForgotPasswordPage = location.pathname === '/forgot-password';
    const isChangePasswordPage = location.pathname === '/change-password';
    const isAuthenticating = authStatus === 'configuring';
    const isAuthenticated = authStatus === 'authenticated';

    useEffect(() => {
        if (isAuthenticated) {
            const redirect = sessionStorage.getItem('loginRedirect');
            if (redirect) {
                sessionStorage.removeItem('loginRedirect');
                try {
                    const to = JSON.parse(redirect);
                    history.push(to);
                } catch (e) {
                    console.error('Failed to parse redirect path:', e);
                }
            }
        }
    }, [isAuthenticated, history]);

    // Allow change password page ONLY if we have a pending challenge user
    // If not authenticated and not on whitelist pages, redirect to login
    if (
        !isAuthenticated &&
        !isLoginPage &&
        !isForgotPasswordPage &&
        !(isChangePasswordPage && challengeUser) &&
        !isAuthenticating
    ) {
        return <Redirect to={{ pathname: '/login', state: { from: location } }} />;
    }

    // Authenticated users should not be on change password page (it is strict force change only)
    if (isAuthenticated && isChangePasswordPage) {
        return <Redirect to="/" />;
    }

    if (isAuthenticating) {
        return <div className="flex h-screen w-full items-center justify-center">Loading...</div>;
    }

    return (
        <div className="App">
            {/* <DataTableProvider> */}
            {!isLoginPage && !isForgotPasswordPage && !isChangePasswordPage && <NavDrawer />}
            <div className={isLoginPage || isForgotPasswordPage || isChangePasswordPage ? '' : 'content'}>
                <Route path="/" exact component={HomeScreen} />

                <Suspense fallback={AuthPageFallback}>
                    <Route path="/login">
                        <Login />
                    </Route>
                    <Route path="/forgot-password">
                        <ForgotPassword />
                    </Route>
                    <Route path="/change-password">
                        <ChangePassword />
                    </Route>
                </Suspense>

                <Route path="/logistics/inbound-trucks" component={InboundTrucks} />
                <Route path="/logistics/outbound-trucks" component={OutboundTrucks} />
                <Route path="/logistics/move-purchased" component={ReassignPurchasedShipments} />
                <Route path="/logistics/bulk-reassign-line-haul" component={BulkReassignLineHaul} />
                <Route path="/logistics/line-haul-management" component={LineHaulManagement} />
                <Route path="/logistics/zip-map" component={ZipMapUpload} />
                <Route path="/logistics/zip-map-results" component={ZipMapResults} />
                <Route path="/logistics/shipping-rates" component={ShippingRateUpload} />
                <Route path="/logistics/manifest-outbound-trucks" component={ManifestTrucks} />
                <Route path="/logistics/carrier-ship-labels" component={PurchaseCarrierShipLabels} />
                <Route path="/logistics/dbi-management" component={DbiManagement} />
                <Route path="/logistics/fbm-zip-map" component={FBMZipMapUpload} />
                <Route path="/logistics/dbi-cross-docks" component={DBICrossDocks} />
                <Route path="/logistics/inbound-cross-docks" component={InboundCrossDocks} />
                <Route
                    exact
                    path="/logistics/cross-dock-purchase-orders/create"
                    component={CreateCrossDockPurchaseOrder}
                />
                <Route exact path="/logistics/cross-dock-purchase-orders" component={CrossDockPurchaseOrders} />
                <Route
                    path="/logistics/view-cross-dock-purchase-order/:id"
                    component={ViewCrossDockPurchaseOrderEntity}
                />
                <Route path="/logistics/holiday-offsets" component={ViewHolidayOffsets} />

                <Route exact path="/custmanage" component={CustManage} />
                <Route path="/custmanage/carrier-lookup" component={CarrierLookup} />
                <Route path="/custmanage/catalog" component={CatalogSummary} />
                <Route path="/custmanage/delay-banners" component={ServiceDelayBanners} />
                <Route path="/custmanage/cm-bulk-tools" component={CustomerManagerBulkTools} />
                <Route path="/custmanage/privacy-requests" component={PrivacyRequests} />

                <Route path="/training" component={Training} />

                <Route path="/fbm/items" component={FBMCatalog} />
                <Route path="/fbm/advance-shipping-notification" component={FBMAsn} />
                <Route path="/fbm/packaging" component={FBMPackagingAsn} />
                <Route path="/fbm/orders" component={FBMViewOrders} />
                <Route path="/fbm/shipments" component={FBMViewShipments} />
                <Route path="/fbm/subs" component={LittleSpoonSubList} />
                <Route path="/fbm/inventory" component={FBMInventory} />
                <Route path="/fbm/hospital" component={ViewFBMShipmentHospital} />
                <Route path="/fbm/ingestion-results" component={FBMIngestionResults} />
                <Route path="/fbm/substitution-rules" component={FBMSubstitutionRules} />
                <Route path="/fbm/reports" component={FBMViewReports} />
                <Route path="/fbm/accounts" component={FBMManageAccounts} />
                <Route path="/fbm/api-docs" component={FBMApiDocs} />
                <Route path="/fbm/user-accounts" component={FBMUserAccounts} />
                <Route path="/fbm/purchase-orders" component={ViewPartnerPurchaseOrdersPage} />
                <Route path="/fbm/ba-perfect-order" component={BAPerfectOrderReportPage} />
                <Route path="/fbm/ls-inventory-planning" component={LittleSpoonInventoryPlanningPage} />

                <Route path="/shipping/batchelor" component={Batchelor} />
                <Route path="/shipping/dbi" component={DBIDeepDive} />
                <Route path="/shipping/reprint" component={BatchelorReprint} />
                <Route path="/shipping/vip" component={PrintLabels} />
                <Route path="/shipping/failed-to-purchase" component={FailedToPurchase} />
                <Route path="/shipping/purchase-label" component={PurchaseLabels} />
                <Route path="/shipping/stuck-shipments" component={StuckShipments} />
                <Route path="/shipping/pickup-boxes" component={PickupBoxes} />
                <Route exact path="/shipping/view-batches" component={ViewBatches} />
                <Route path="/shipping/special-orders">
                    <SpecialOrders editable={false} />
                </Route>

                <Route path="/warehouse/locations" component={LocationsV2} />
                <Route path="/warehouse/mispick-handling" component={MispickHandling} />
                <Route path="/warehouse/netsuite" component={Netsuite} />
                <Route path="/warehouse/oos-log" component={OosLogPage} />
                <Route path="/warehouse/pack-lines" component={PackLines} />
                <Route path="/warehouse/pack-line-zones" component={PackLineZones} />
                <Route path="/warehouse/rack-labels" component={RackLabels} />
                <Route path="/warehouse/cube-levels" component={CubeLevels} />
                <Route path="/warehouse/inventory" component={LocationInventory} />
                <Route exact path="/warehouse/fc-transfers" component={ViewFCTransfers} />
                <Route exact path="/warehouse/all-fc-transfers" component={ViewAllFCTransfers} />
                <Route exact path="/warehouse/accept-fc-transfer/:transferId" component={ViewAcceptTransfer} />
                <Route exact path="/warehouse/fc-transfers/:transferId" component={ViewFCTransferEntity} />
                <Route exact path="/warehouse/create-fc-transfer" component={CreateFCTransfer} />
                <Route path="/warehouse/internal-transfers" component={InternalTransfers} />
                <Route path="/warehouse/slotting" component={Slotting} />
                <Route path="/warehouse/prep" component={InventoryPrepTabs} />
                <Route path="/warehouse/inventory-separation" component={InventorySeparation} />
                <Route path="/warehouse/feature-flag-management" component={FeatureFlagManagement} />
                <Route path="/ops-visibility/inventory-history" component={InventoryHistoryV2} />
                <Route path="/ops-visibility/cycle-count" component={CycleCount} />
                <Route path="/ops-visibility/negative-variance" component={NegativeVariance} />
                <Route path="/ops-visibility/shrink-log" component={ShrinkLog} />
                <Route path="/ops-visibility/bams-shorts" component={BamsShorts} />

                <Route path="/receiving/receiving" component={Receiving} />
                <Route path="/receiving/documents" component={PurchaseOrderDocuments} />
                <Route path="/receiving/documents-v2" component={PurchaseOrderDocumentsV2} />
                <Route path="/receiving/inbound-receiving" component={InboundReceiving} />

                <Route path="/catalog/item-information" component={ItemInformation} />
                <Route path="/catalog/bundles" component={Bundles} />
                <Route path="/catalog/item-information-upload" component={ItemInformationUpload} />

                <Route path="/marketing/referralProgram" component={ReferralProgram} />
                <Route path="/marketing/discounts" component={Discounts} />
                <Route path="/marketing/specialOrders" component={SpecialOrdersMarketing} />
                <Route path="/marketing/produceList" component={ProduceList} />
                <Route path="/marketing/giftCodes" component={SystemGiftCodes} />
                <Route path="/marketing/vip-customers" component={VipCustomers} />
                <Route path="/marketing/acquisition-pdps" component={AcquisitionPDPs} />
                <Route path="/marketing/search-boost" component={SearchBoostComponent} />

                <Route path="/procurement/viewShippers" component={Shippers} />
                <Route exact path="/procurement/viewPurchaseOrder-v2" component={ViewPurchaseOrdersV2} />
                <Route path="/procurement/viewPurchaseOrder-v2/:purchaseOrderId" component={ViewPurchaseOrderEntity} />
                <Route path="/procurement/createPurchaseOrder-v2" component={CreatePurchaseOrder} />
                <Route path="/procurement/bulkCreatePurchaseOrders-v2" component={BulkCreatePurchaseOrder} />
                <Route path="/procurement/taxonomy" component={Taxonomy} />
                <Route path="/procurement/items" component={Catalog} />
                <Route path="/procurement/catalog-events" component={CatalogEventsPage} />
                <Route path="/procurement/vendor" component={Vendor} />
                <Route path="/procurement/credits-tracking" component={CreditsTracking} />

                <Route path="/partnerships/drinks" component={Drinks} />
                <Route path="/partnerships/order-import" component={ViewFAMOrders} />
                <Route path="/partnerships/order-sources" component={FAMPartners} />

                <Route path="/production/sellable-items" component={SellableItems} />
                <Route path="/production/sellable-item-prices" component={SellableItemPrices} />
                <Route path="/production/pricing-zones" component={PricingZones} />
                <Route path="/production/sellable-item-inventory" component={SellableItemInventory} />
                <Route path="/production/misfits-plus-discounts" component={SellableItemMisfitsPlusDiscounts} />
                <Route path="/production/carousels" component={Carousels} />
                <Route path="/production/carousel-upload" component={CarouselUpload} />
                <Route path="/production/pack-groups" component={PackGroups} />
                <Route path="/production/pricing" component={ViewPricing} />
                <Route path="/production/banner-creation" component={BannerCreation} />
                <Route path="/production/storefront-configurations" exact component={StorefrontConfigList} />
                <Route path="/production/storefront-configurations/:id" component={StorefrontConfigEditor} />
                <Route path="/production/search-config" component={SearchConfig} />
                <Route path="/production/aisle-content" component={AisleContent} />

                <Route path="/quality/receiving" component={QualityControlReceiving} />
                <Route path="/quality/ccof" component={ViewCCOF} />
                <Route path="/quality/view-qc-audit-tasks" component={ViewQCAuditTasks} />

                <Route path="/compensations/mass-credit" component={MassCredit} />

                <Route path="/packaging/assign" component={AssignPackaging} />
                <Route path="/packaging/auto-assign" component={AutoAssignPackaging} />
                <Route path="/packaging/packaging" component={Packaging} />
                <Route exact path="/packaging/viewPurchaseOrder-v2" component={ViewPackagingPOV2} />
                <Route path="/packaging/viewPurchaseOrder-v2/:purchaseOrderId" component={ViewPackagingPOEntity} />
                <Route path="/packaging/createPurchaseOrder-v2" component={CreatePackagingPOV2} />
                <Route path="/packaging/bulkCreatePurchaseOrders-v2" component={BulkCreatePackagingPOV2} />
                <Route path="/packaging/purchase" component={PackagingPurchaseOrder} />
                <Route path="/packaging/weather-pull" component={WeatherReport} />

                <Route path="/product/feature-flag" component={FeatureFlag} />
                <Route path="/product/cohorts" component={Cohort} />
                <Route path="/product/plus-membership-offers" component={PlusMembershipOffers} />
                <Route path="/product/cart-experience" component={CartExperience} />
                <Route path="/product/driver-tips-settings" component={DriverTipsSettings} />
                <Route path="/product/out-of-box-availability" component={OutOfBoxAvailability} />

                <Route path="/opsVisibility/shipment-status" component={ShipmentStatus} />
                <Route path="/opsVisibility/fulfillment-lifecycle" component={FulfillmentLifecycleDashboard} />

                <Route path="/shopperMarketing/vendor-promotions" component={VendorPromotions} />

                <Route path="/system/ccpa-dashboard" component={CCPADashboard} />
                <Route path="/system/marketplace-inventory" component={MarketplaceInventory} />
                <Route path="/system/marketplace-drinks-inventory" component={MarketplaceDrinksInventory} />
                <Route path="/system/order-deep-dive" component={OrderDeepDive} />
                <Route path="/system/live-inventory" component={LiveInventory} />

                <Route path="/infoTech/jalapenoAccounts" component={JalapenoAccounts} />
                <Route path="/infoTech/jalapenoProfiles" component={ViewJalapenoProfiles} />
                <Route path="/infoTech/ApproverViewJalapenoProfiles" component={ViewOnlyJalapenoProfiles} />
                <Route path="/infoTech/employeeBox" component={EmployeeBox} />
                <Route path="/infoTech/food-as-medicine" component={FoodAsMedicineAccounts} />
                <Route path="/infoTech/its-knowledge-base" component={ITSKnowledgeBase} />
                <Route path="/infoTech/netsuite-ai" component={NetSuiteAI} />

                <Route path="/growth/campaigns" component={EmailCampaigns} />
                <Route path="/growth/nps" component={NPSResults} />

                <Route path="/finance/driver-tip-finance" component={DriverTipsReports} />
                <Route path="/finance/payments" component={Payments} />
                <Route path="/finance/uploads" component={FinanceUploads} />

                <Route path="/gopuff/batches" component={ViewGoPuffBatches} />
                <Route path="/gopuff/micro-fc" component={GoPuffMicroFC} />
                <Route path="/gopuff/orders" component={GoPuffOrderViewer} />
                <Route path="/gopuff/order-upload" component={WholesaleOrderUpload} />
                <Route path="/gopuff/packout" component={ViewGoPuffPackout} />
                <Route path="/gopuff/invoices" component={ViewGoPuffInvoices} />
                <Route path="/gopuff/pricing" component={ViewGoPuffPricing} />
                <Route path="/gopuff/pricing-upload" component={WholesalePricingUpload} />
                <Route path="/gopuff/adjustments-upload" component={WholesaleAdjustmentUpload} />
                <Route path="/gopuff/issue-po-credits" component={IssueGoPuffCredits} />
                <Route path="/gopuff/tms-upload" component={GoPuffTMSUpload} />
                <Route path="/gopuff/review-po-credits" render={(props) => <ReviewWholesaleCredits {...props} />} />
            </div>
            {/* </DataTableProvider> */}
        </div>
    );
}

function AppSelector() {
    const location = useLocation();
    const [showFamApp, setShowFamApp] = useState(location.pathname.includes('/partners/'));
    const { user } = useContext(UserContext);
    const { authStatus } = useAuth();
    const isAuthenticated = authStatus === 'authenticated';

    useEffect(() => {
        setShowFamApp(location.pathname.includes('/partners/'));
    }, [location.pathname]);

    if (!isAuthenticated) {
        return <MMApp />;
    }

    if (user.famPartner === true || showFamApp) {
        return <FamApp />;
    }

    // Default to MMApp which handles authentication checks for generic users
    return <MMApp />;
}

export default App;
